<template>
  <span ref="triggerEl" class="floating-popover-trigger">
    <slot />
    <Teleport v-if="modelValue" to="body">
      <div
        ref="contentEl"
        class="floating-popover-content z-(--z-popover)"
        :class="contentClass"
        :style="contentStyle"
      >
        <slot name="content" :close="close" :flipped="flipped" :available-space="availableSpace" />
      </div>
    </Teleport>
  </span>
</template>

<script setup>
import { ref, computed, nextTick, watch, onUnmounted } from 'vue';
import { onClickOutside, useEventListener } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { useFloatingPosition } from '@/composables/useFloatingPosition';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  placement: {
    type: String,
    default: 'bottom', // 'bottom' | 'top' — preferred side, flips when it doesn't fit
  },
  align: {
    type: String,
    default: 'center', // 'start' | 'center' | 'end' — matches Dropdown's stickLeft/stickRight
  },
  gap: {
    type: Number,
    default: 4,
  },
  contentClass: {
    type: String,
    default: '',
  },
  closeOnClickOutside: {
    type: Boolean,
    default: true,
  },
  closeOnEscape: {
    type: Boolean,
    default: true,
  },
  // Escape hatches for consumers with extra positioning needs (e.g. Dropdown's
  // iOS-keyboard-aware viewport and dynamic-height menu lists).
  noFlip: {
    type: Boolean,
    default: false,
  },
  preferMoreSpace: {
    type: Boolean,
    default: false,
  },
  getBounds: {
    type: Function,
    default: undefined,
  },
  getFitHeight: {
    type: Function,
    default: undefined,
  },
  // Re-run positioning on scroll/resize while open. Consumers with a platform-specific
  // reason to hold position static (e.g. Dropdown skips this on iOS, where retracking
  // against visualViewport changes previously caused jank — see git history) can disable it.
  trackOnScroll: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const router = useRouter();
const triggerEl = ref(null);
const contentEl = ref(null);
const ready = ref(false);

const { style: positionStyle, flipped, availableSpace, update, startTracking, stopTracking } = useFloatingPosition(
  triggerEl,
  contentEl,
  props,
);

// either side can change size while the panel is open — a combobox field wraps onto a
// second row as chips are picked, which moves the edge the panel is anchored to, and the
// panel grows as its content changes (e.g. a create option appears), so it may need to flip
const sizeObserver = new ResizeObserver(() => {
  update();
});

const contentStyle = computed(() => ({
  ...positionStyle.value,
  visibility: ready.value ? 'visible' : 'hidden',
}));

const close = () => {
  if (props.modelValue) emit('update:modelValue', false);
};

// a closed popover sits on every task card of a board, so window listeners exist only while it is open
let stopWindowListeners = [];
const removeWindowListeners = () => {
  stopWindowListeners.forEach(stop => stop());
  stopWindowListeners = [];
};

watch(() => props.modelValue, async (isOpen) => {
  if (isOpen) {
    stopWindowListeners = [
      // `ignore` relies on pointerdown reaching window, but task card parts stop it (so a click
      // does not start a drag), so a click on the trigger is ignored here explicitly
      onClickOutside(contentEl, (event) => {
        if (!props.closeOnClickOutside || triggerEl.value.contains(event.target)) return;
        close();
      }, { ignore: [triggerEl] }),
      useEventListener(window, 'keydown', (e) => {
        if (e.key !== 'Escape' || !props.closeOnEscape) return;
        close();
        e.stopPropagation();
      }, { capture: true }),
      // a page that opens over the board keeps it mounted, so Back/Forward would leave the panel
      // open over the new page; a query-only change (a report filter picked in an open combobox) keeps it
      router.afterEach((to, from, failure) => {
        if (!failure && to.path !== from.path) close();
      }),
    ];
    ready.value = false;
    await nextTick();
    if (!props.modelValue) return;
    // First pass measures natural content size and may trigger a consumer-side
    // style change (e.g. Dropdown's availableSpace-driven max-height). Settle
    // with a second pass once that's applied, before revealing, so there's no
    // visible jump from a since-clamped height changing the flip/position.
    update();
    await nextTick();
    if (!props.modelValue) return;
    update();
    ready.value = true;
    sizeObserver.observe(triggerEl.value);
    sizeObserver.observe(contentEl.value);
    if (props.trackOnScroll) startTracking();
  } else {
    ready.value = false;
    sizeObserver.disconnect();
    stopTracking();
    removeWindowListeners();
  }
});

onUnmounted(() => {
  sizeObserver.disconnect();
  stopTracking();
  removeWindowListeners();
});

defineExpose({ update, close });
</script>
